import React, {
    Dispatch,
    FC,
    SetStateAction,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import { useMatch } from 'react-router';
import { Modal, message } from 'antd';
import { observer } from 'mobx-react';
import { AxiosError } from 'axios';

import i18n from '@/content';
import { MFA_ATTEMPTS } from '@/consts';
import TabTitle from '../Common/TabTitle';
import Spinner from '../Common/Spin';
import ViewerErrorResult from './ViewerErrorResult';
import LoadErrorReportPage from '../Common/LoadErrorReport/LoadErrorReportPage';
import { buildErrorDiagnostics } from '../Common/LoadErrorReport/buildErrorDiagnostics';
import { HELPDESK_EMAILS_MAILTO } from '@/config/support';
import MfaComp from '../AppAuthenticator/MfaComp';
import AttemptsRemainWarning from './AttemptsRemainWarning';
import DownloadFile from './DownloadFile';
import OpenWidget from './OpenWidget';
import { useStores } from '../hooks';
import { BASEURL, ENDPOINTS, getFile } from '../../api';
import appConfig from '../../config/env';
import { IS_MOBILE } from '@/config/browserEnv';
import { OpenOptions } from '@/config/openOptions';
import { FileAccessResponse, OfficeViewerResponse, OpenFileResponse, SecuredFile } from '@/types/types';
import {
    ErrorTypes,
    IframeParams,
    PermissionsConfig,
} from './interfaces';
import {
    preventTimeout,
    captureServerError,
    splitFilename,
    preventTimeoutOpenFile,
} from '../utils';
import {
    checkEditPermissions,
    getErrorType,
    showDownloadErrorMessage,
} from './helpers';
import { DOWNLOAD_FATAL_ERRORS, MOBILE_VIEW_UNSUPPORTED_EXTENSIONS } from './constants';
import styles from './OpenViewer.module.scss';
import { VIEWER_ROUTE_TEMPLATE } from '@/config/appRoutes';
import MFAPasswordModal from '@/components/Common/MFAPasswordModal';

type AccessType = OpenOptions.viewer | OpenOptions.download;

const OpenViewer: FC = observer(() => {
    const [iframeParams, setIframeParams] = useState<IframeParams>(null);
    // TODO: migrate logic on FileAccessStore
    const [isMfa, setIsMfa] = useState<boolean>(false);
    const [isMfaVisible, setIsMfaVisible] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isDownloadButtonLoading, setIsDownloadButtonLoading] = useState<boolean>(false);
    const [errorType, setErrorType] = useState<ErrorTypes>(null);
    const [loadError, setLoadError] = useState<unknown>(null);
    const [attemptsCounter, setAttemptsCounter] = useState<number>(MFA_ATTEMPTS);
    const [file, setFile] = useState<SecuredFile>(null);

    const nextActionAfterMFARef = useRef<AccessType>(OpenOptions.viewer);

    const {
        authSettingsStore,
        userStore,
        batchDownloadStore,
    } = useStores();

    const permissionsConfig = useMemo<PermissionsConfig>(() => {
        const editAllowed = checkEditPermissions(userStore.currentUserEmail, file);
        return {
            editAllowed,
            downloadAllowed: !appConfig.viewer?.HIDE_DOWNLOAD_BUTTON
                && file?.menu?.options?.includes(OpenOptions.download),
        };
    }, [file]);

    const { API, skipMFA } = authSettingsStore;

    const match = useMatch(VIEWER_ROUTE_TEMPLATE);
    const fileId = match.params.id;

    const closeMfa = (): void => {
        setIsMfaVisible(false);
        setIsMfa(false);
        setAttemptsCounter(MFA_ATTEMPTS);
    };

    const takeAwayAttempt = (): void => {
        setIsMfaVisible(true);
        setAttemptsCounter((prevState) => prevState - 1);
    };

    const showMFA = (): void => {
        setIsMfa(true);
        setIsMfaVisible(true);
    };

    const handleFetchFileError = (
        error: unknown,
        onError: (errorType: ErrorTypes) => void,
        mfaCode?: string,
    ): void => {
        const newErrorType = getErrorType(error as AxiosError);
        const shouldTakeAwayAttempt = newErrorType === 'mfaFail' && attemptsCounter > 1;
        if (!skipMFA && newErrorType === 'mfaRequired') {
            showMFA();
        } else if (
            !skipMFA
            && newErrorType === 'mfaPasswordRequired'
            && nextActionAfterMFARef.current === OpenOptions.viewer
        ) {
            batchDownloadStore.setIsMFAPasswordMounted(true);
            batchDownloadStore.setIsMFAPasswordVisible(true);
        } else {
            // eslint-disable-next-line no-console
            console.log('could not load file', error);
            captureServerError(error as Error, 'OpenViewer.handleFetchFileError');
            setLoadError(error);
            if (shouldTakeAwayAttempt) {
                takeAwayAttempt();
            } else {
                onError(newErrorType);
                if (mfaCode) {
                    closeMfa();
                }
            }
        }
    };

    const fetchAccessLink = async <Response, >(
        appName: AccessType,
        setLoader: Dispatch<SetStateAction<boolean>>,
        onSuccess: (response: Response) => void,
        onError: (errorType: ErrorTypes) => void,
        mfaCode = '',
    ): Promise<void> => {
        setLoader(true);
        const requestBody = { app_name: appName, auth_code: mfaCode };
        try {
            const requestCallback = async (): Promise<Response> => API.post(BASEURL.backend(),
                ENDPOINTS.openFile(fileId),
                { body: requestBody });
            const response: Response = await preventTimeout(requestCallback);
            onSuccess(response);
            if (mfaCode) {
                closeMfa();
            }
        } catch (error) {
            handleFetchFileError(error, onError, mfaCode);
        } finally {
            setLoader(false);
        }
    };

    const fetchIframeParams = async (mfaCode = ''): Promise<void> => {
        await fetchAccessLink<IframeParams>(
            OpenOptions.viewer,
            setIsLoading,
            setIframeParams,
            setErrorType,
            mfaCode,
        );
    };

    const onDownloadError = (error: ErrorTypes): void => {
        if (DOWNLOAD_FATAL_ERRORS.includes(error)) {
            setErrorType(error);
        } else {
            showDownloadErrorMessage(error, i18n.t.bind(i18n));
        }
    };

    const fetchDownloadLink = async (mfaCode = ''): Promise<string> => {
        let downloadLink: string;
        await fetchAccessLink<OpenFileResponse>(
            OpenOptions.download,
            setIsDownloadButtonLoading,
            ({ link }: OpenFileResponse) => {
                downloadLink = link;
            },
            onDownloadError,
            mfaCode,
        );
        return downloadLink;
    };

    const executeDownload = useCallback(async (): Promise<string> => {
        nextActionAfterMFARef.current = OpenOptions.download;
        return fetchDownloadLink();
    }, []);

    const onMfaClick = useCallback(async (mfaCode: string): Promise<void> => {
        // First, verify phone MFA with backend
        let verifySuccess = false;
        try {
            const response = await API.post(
                BASEURL.backend(),
                ENDPOINTS.verifyFileAccess(),
                {
                    body: { method: 'phone', attempt: mfaCode },
                },
            );
            verifySuccess = response?.success === true;
        } catch (error) {
            console.error('MFA verification request failed:', error);
            verifySuccess = false;
        }

        if (!verifySuccess) {
            // Phone code incorrect - show error and handle attempts
            message.error(i18n.t('recipientPage.errors.notAuthorisedCode'));
            if (attemptsCounter > 1) {
                takeAwayAttempt();
            } else {
                setErrorType('mfaFail');
                closeMfa();
            }
            return;
        }

        // Phone verification succeeded, now close modal and proceed with file access
        // Errors from fetchDownloadLink/fetchIframeParams are handled by their own error handlers
        setIsMfaVisible(false);
        if (nextActionAfterMFARef.current === OpenOptions.download) {
            await fetchDownloadLink(mfaCode);
        } else {
            await fetchIframeParams(mfaCode);
        }
    }, [attemptsCounter]);

    const onMetaDataLoaded = (fileData: SecuredFile): void => {
        const { filename } = fileData;
        setFile(fileData);
        if (filename && IS_MOBILE && !appConfig.viewer?.CANCEL_MOBILE_VIEWER_FILE_EXTENSION_CHECK) {
            const { extension } = splitFilename(filename);
            if (MOBILE_VIEW_UNSUPPORTED_EXTENSIONS.includes(extension.toLowerCase())) {
                setErrorType('mobileView');
            }
        }
    };

    const fetchFileData = async (): Promise<void> => {
        const fileData = await getFile(API, fileId);
        onMetaDataLoaded(fileData);
    };

    const retryOpenViewer = useCallback(async (): Promise<void> => {
        // Silent background retry: never flip to a loading/spinner state while a
        // 408 persists. Only leave the screen once the file is actually ready,
        // or surface a genuinely different error.
        try {
            const params = await API.post(
                BASEURL.backend(),
                ENDPOINTS.openFile(fileId),
                { body: { app_name: OpenOptions.viewer } },
            );
            setIframeParams(params);
            const ready = !!(params as OfficeViewerResponse).viewUrl
                || !!(params as FileAccessResponse).link;
            if (ready) {
                setLoadError(null);
                setErrorType(null);
                setIsLoading(false);
            }
        } catch (error) {
            const next = getErrorType(error as AxiosError);
            if (next !== 'fileBeingPrepared') {
                setLoadError(error);
                setErrorType(next);
            }
        }
    }, [API, fileId]);

    const preparationDiagnostics = useMemo(() => buildErrorDiagnostics({
        fileIds: [fileId],
        user: userStore.currentUserEmail,
        userId: userStore.currentUserId,
        error: loadError ?? undefined,
        statusCode: 408,
        message: 'File is being prepared',
    }), [fileId, loadError, userStore.currentUserEmail, userStore.currentUserId]);

    useEffect(() => {
        if (fileId) {
            (async (): Promise<void> => {
                setIsLoading(true);
                const requestCallback = async (): Promise<IframeParams> => API.post(BASEURL.backend(),
                    ENDPOINTS.openFile(fileId),
                    { body: { app_name: OpenOptions.viewer } });
                try {
                    const [iframeViewerParams] = await Promise.all([
                        preventTimeoutOpenFile(requestCallback), fetchFileData(),
                    ]);
                    setIframeParams(iframeViewerParams);
                    if ((iframeViewerParams as OfficeViewerResponse).viewUrl) {
                        setIsLoading(false);
                    }
                } catch (error) {
                    setIsLoading(false);
                    handleFetchFileError(error, setErrorType);
                }
            })();
        }
    }, []);

    const hasDownload = permissionsConfig.downloadAllowed;
    const policyId = file?.policy?.id;

    return (
        <div className={styles['viewer-wrapper']}>
            <TabTitle title={i18n.t('viewer.tabTitle')} />
            {errorType === 'fileBeingPrepared'
                ? (
                    <div className={styles['securing-wrapper']}>
                        <LoadErrorReportPage
                            variant="408"
                            fileId={fileId}
                            diagnostics={preparationDiagnostics}
                            helpdeskRecipients={HELPDESK_EMAILS_MAILTO}
                            onPreparationRetry={retryOpenViewer}
                            compact
                        />
                    </div>
                )
                : errorType
                    ? (
                        <ViewerErrorResult
                            errorType={errorType}
                            ownerEmail={file?.owner_email}
                            itemType={file?.is_folder ? 'folder' : 'file'}
                        />
                    )
                    : (
                    <Spinner
                        tip={i18n.t('general.waitMessages.preparingFile')}
                        size="large"
                        spinning={isLoading}
                        wrapperClassName={styles['spinner-wrapper']}
                    >
                        {isMfa && (
                            <Modal
                                open={isMfaVisible}
                                centered
                                footer={null}
                                maskClosable={false}
                                onCancel={closeMfa}
                                destroyOnClose
                            >
                                <>
                                    {attemptsCounter < MFA_ATTEMPTS && (
                                        <AttemptsRemainWarning attemptsCounter={attemptsCounter} />
                                    )}
                                    <MfaComp onMfaClick={onMfaClick} />
                                </>
                            </Modal>
                        )}
                        {batchDownloadStore.isMFAPasswordMounted && (
                            <MFAPasswordModal
                                // For viewer flows: after successful password verification,
                                // re-trigger the viewer /open request without passing the password.
                                onSuccessAfterVerify={async () => {
                                    await fetchIframeParams();
                                }}
                                onAttemptsExhausted={() => {
                                    setErrorType('mfaPasswordFail');
                                }}
                            />
                        )}
                        {hasDownload && (
                            <DownloadFile
                                runDownload={executeDownload}
                                isLoading={isDownloadButtonLoading}
                                ownerEmail={file?.owner_email}
                                filename={file?.filename}
                                filePolicyId={policyId}
                            />
                        )}
                        <OpenWidget
                            isEditable={permissionsConfig.editAllowed}
                            fileId={fileId}
                            filePolicyId={policyId}
                            setIsLoading={setIsLoading}
                            iframeParams={iframeParams}
                            PDFViewerClassName={styles['pdf-viewer']}
                        />
                    </Spinner>
                    )}
        </div>
    );
});

export default OpenViewer;
